"use client";

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
  Field,
  Input,
  Label,
  NavbarItem,
  Select,
  SidebarItem,
  SidebarLabel,
  Textarea,
} from "@/components";
import { CENTI_SUPPORT } from "@/lib/constants/support";
import { getErrorMessage } from "@/lib/utils";
import {
  CheckCircleIcon,
  ChatBubbleLeftRightIcon,
  PhoneArrowDownLeftIcon,
  PhoneIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { usePathname, useRouter } from "next/navigation";
import React, { FormEvent, useRef, useState } from "react";
import { FaWhatsapp } from "react-icons/fa6";
import { toast } from "react-toastify";
import { requestSupportCallback } from "../services";
import { completeActivationStep } from "@/app/(procure)/procure/get-started/services";

type ContactCentiProps = {
  defaultPhone?: string | null;
  stepNumber?: number;
  /**
   * - quicklink: numbered row on the dashboard (opens a dialog)
   * - desktop / mobile: nav triggers (open a dialog)
   * - inline: options rendered directly in the page (Get Started #contact)
   */
  variant?: "quicklink" | "desktop" | "mobile" | "inline";
  /** When the activation `contact_support` step is already done, skip recording it. */
  contactStepDone?: boolean;
  /**
   * Custom trigger for the dialog (e.g. a Get Started checklist row). Contact
   * made through it updates the checklist once the dialog is closed.
   */
  renderTrigger?: (open: () => void) => React.ReactNode;
};

/**
 * Fire-and-forget: mark the `contact_support` activation step as done when a
 * customer reaches out. Never blocks navigation and never surfaces errors.
 * Only revalidates (and so re-renders) when asked, i.e. on Get Started.
 */
const recordContact = (
  source: "whatsapp" | "phone" | "callback",
  revalidate: boolean,
) => {
  try {
    void completeActivationStep("contact_support", source, {
      revalidate,
    }).catch(() => {});
  } catch {
    // ignore — tracking must never break contacting support
  }
};

type View = "options" | "callback" | "success";

export default function ContactCenti({
  defaultPhone,
  stepNumber,
  variant = "quicklink",
  contactStepDone = false,
  renderTrigger,
}: ContactCentiProps) {
  const pathname = usePathname();
  const router = useRouter();
  const isInline = variant === "inline";
  // Opened from a checklist row: the checklist may disappear once this step
  // completes, so its refresh waits until the dialog is closed.
  const hasCustomTrigger = Boolean(renderTrigger);
  const refreshOnClose = useRef(false);
  const onContact = (source: "whatsapp" | "phone") => {
    if (contactStepDone) return;
    recordContact(source, isInline);
    if (hasCustomTrigger) refreshOnClose.current = true;
  };
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<View>("options");
  const [phoneNumber, setPhoneNumber] = useState(defaultPhone || "");
  const [preferredTime, setPreferredTime] = useState("As soon as possible");
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const close = () => {
    setIsOpen(false);
    setView("options");
    if (refreshOnClose.current) {
      refreshOnClose.current = false;
      router.refresh();
    }
  };

  const submitCallbackRequest = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);

    const response = await requestSupportCallback({
      phoneNumber: phoneNumber.trim(),
      preferredTime,
      note: note.trim() || undefined,
      source: pathname,
    });

    setIsSubmitting(false);

    if (!response.ok) {
      toast.error(
        getErrorMessage(response) ||
          "We couldn't submit your request. Please call or message us instead.",
      );
      return;
    }

    setView("success");
    setNote("");
    // The backend records callback requests too; on Get Started, also
    // complete the step explicitly so the checklist updates immediately
    // (the server action revalidates /procure/get-started, which refreshes).
    if (!contactStepDone && (isInline || hasCustomTrigger)) {
      await completeActivationStep("contact_support", "callback", {
        revalidate: isInline,
      }).catch(() => null);
      if (hasCustomTrigger) refreshOnClose.current = true;
    }
  };

  if (
    variant !== "quicklink" &&
    variant !== "inline" &&
    pathname !== "/" &&
    !pathname.startsWith("/procure")
  ) {
    return null;
  }

  const openContactOptions = () => setIsOpen(true);

  const trigger = renderTrigger ? (
    renderTrigger(openContactOptions)
  ) : variant === "desktop" ? (
    <NavbarItem
      onClick={openContactOptions}
      aria-label="Contact Centi"
      title="Contact Centi">
      <ChatBubbleLeftRightIcon className="size-6 text-zinc-600 dark:text-zinc-300" />
    </NavbarItem>
  ) : variant === "mobile" ? (
    <SidebarItem onClick={openContactOptions}>
      <ChatBubbleLeftRightIcon className="size-5 text-zinc-500" />
      <SidebarLabel>Contact Centi</SidebarLabel>
    </SidebarItem>
  ) : (
    <NavbarItem onClick={openContactOptions}>
      <span className="inline-flex h-14 w-14 items-center justify-center rounded-md bg-white shadow dark:bg-zinc-900 lg:h-10 lg:w-10">
        {stepNumber ? (
          <span className="text-lg text-zinc-800 dark:text-white">
            {stepNumber}
          </span>
        ) : (
          <ChatBubbleLeftRightIcon
            className="size-7 text-zinc-800 dark:text-white lg:size-5"
            aria-hidden="true"
          />
        )}
      </span>
      <span className="flex flex-col max-sm:space-y-1">
        <span className="font-semibold">Contact Centi</span>
        <span className="text-[15px] font-normal leading-5 text-gray-600 dark:text-gray-200 lg:pt-0.5">
          Call, WhatsApp, or request a callback
        </span>
      </span>
    </NavbarItem>
  );

  const content = (
    <>
      {view === "options" && (
        <DialogBody className="space-y-3">
          <ContactOption
            href={CENTI_SUPPORT.whatsappUrl}
            target="_blank"
            icon={<FaWhatsapp className="size-6 text-green-600" />}
            title="Chat on WhatsApp"
            description={CENTI_SUPPORT.phoneDisplay}
            onClick={() => onContact("whatsapp")}
          />
          <ContactOption
            href={`tel:${CENTI_SUPPORT.phoneDial}`}
            icon={<PhoneIcon className="size-6 text-blue-600" />}
            title="Call Centi"
            description={CENTI_SUPPORT.phoneDisplay}
            onClick={() => onContact("phone")}
          />
          <button
            type="button"
            className="flex w-full items-center gap-4 rounded-lg border border-zinc-200 p-4 text-left transition hover:border-blue-300 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-zinc-700 dark:hover:bg-zinc-800"
            onClick={() => setView("callback")}>
            <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-orange-50 dark:bg-zinc-700">
              <PhoneArrowDownLeftIcon className="size-6 text-orange-600" />
            </span>
            <span>
              <span className="block font-semibold text-zinc-950 dark:text-white">
                Request a callback
              </span>
              <span className="mt-0.5 block text-sm text-zinc-600 dark:text-zinc-300">
                Tell us when to call you
              </span>
            </span>
          </button>
          <p className="pt-1 text-center text-xs text-zinc-500">
            You can also email {CENTI_SUPPORT.email}
          </p>
        </DialogBody>
      )}

      {view === "callback" && (
        <form onSubmit={submitCallbackRequest}>
          <DialogBody className="space-y-4">
            <p className="text-sm text-zinc-600 dark:text-zinc-300">
              Share a convenient time and our support team will contact you.
            </p>
            <Field>
              <Label>Phone number</Label>
              <Input
                type="tel"
                value={phoneNumber}
                onChange={(event) => setPhoneNumber(event.target.value)}
                placeholder="+234 801 234 5678"
                autoComplete="tel"
                minLength={8}
                maxLength={24}
                required
              />
            </Field>
            <Field>
              <Label>When should we call?</Label>
              <Select
                value={preferredTime}
                onChange={(event) => setPreferredTime(event.target.value)}>
                <option>As soon as possible</option>
                <option>Today, 9 AM–12 PM WAT</option>
                <option>Today, 12 PM–5 PM WAT</option>
                <option>Next business day, 9 AM–12 PM WAT</option>
                <option>Next business day, 12 PM–5 PM WAT</option>
              </Select>
            </Field>
            <Field>
              <Label>How can we help? (optional)</Label>
              <Textarea
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="For example: I need help with a sourcing request"
                rows={3}
                maxLength={500}
              />
            </Field>
          </DialogBody>
          <DialogActions>
            <Button
              type="button"
              outline
              onClick={() => setView("options")}
              disabled={isSubmitting}>
              Back
            </Button>
            <Button type="submit" color="blue" disabled={isSubmitting}>
              <PhoneArrowDownLeftIcon />
              {isSubmitting ? "Sending request…" : "Request callback"}
            </Button>
          </DialogActions>
        </form>
      )}

      {view === "success" && (
        <DialogBody className="text-center">
          <CheckCircleIcon className="mx-auto size-14 text-green-600" />
          <p className="mt-4 font-semibold text-zinc-950 dark:text-white">
            We’ve received your request.
          </p>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">
            A member of the Centi team will call {phoneNumber} at your chosen
            time.
          </p>
          <Button className="mt-6" color="blue" onClick={close}>
            Done
          </Button>
        </DialogBody>
      )}
    </>
  );

  if (isInline) {
    return (
      <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10 sm:p-6">
        <h3 className="text-base font-semibold text-zinc-950 dark:text-white">
          {view === "callback"
            ? "Request a callback"
            : view === "success"
              ? "Callback requested"
              : "Talk to the Centi team"}
        </h3>
        {view === "options" && (
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            Say hello — we'll help you place your first order and answer any
            questions.
          </p>
        )}
        {content}
      </div>
    );
  }

  return (
    <>
      {trigger}

      <Dialog centered size="md" open={isOpen} onClose={close}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <DialogTitle>
              {view === "callback"
                ? "Request a callback"
                : view === "success"
                  ? "Callback requested"
                  : "Contact Centi"}
            </DialogTitle>
            {view === "options" && (
              <DialogDescription>
                Choose the most convenient way to reach our support team.
              </DialogDescription>
            )}
          </div>
          <Button plain onClick={close} aria-label="Close contact options">
            <XMarkIcon className="!size-6" />
          </Button>
        </div>

        {content}
      </Dialog>
    </>
  );
}

function ContactOption({
  href,
  target,
  icon,
  title,
  description,
  onClick,
}: {
  href: string;
  target?: "_blank";
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick?: () => void;
}) {
  return (
    <a
      href={href}
      target={target}
      rel={target === "_blank" ? "noopener noreferrer" : undefined}
      onClick={onClick}
      className="flex w-full items-center gap-4 rounded-lg border border-zinc-200 p-4 text-left transition hover:border-blue-300 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-zinc-700 dark:hover:bg-zinc-800">
      <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-zinc-50 dark:bg-zinc-700">
        {icon}
      </span>
      <span>
        <span className="block font-semibold text-zinc-950 dark:text-white">
          {title}
        </span>
        <span className="mt-0.5 block text-sm text-zinc-600 dark:text-zinc-300">
          {description}
        </span>
      </span>
      <ChatBubbleLeftRightIcon className="ml-auto size-5 text-zinc-400" />
    </a>
  );
}
